/**
 * Styles shared by the expandable property card + units table pattern, used by both
 * FlexiblePropertiesPage and ListingListPage. Page-specific pieces (column widths,
 * per-page cell styles) stay in each page's own styles module.
 */
import { css } from '@/styled-system/css';

export const cardStyles = css.raw({
  padding: 'none',
  overflow: 'hidden',
  borderColor: 'border.muted',
});

export const headerStyles = css.raw({
  display: 'flex',
  alignItems: 'center',
  gap: '400',
  flex: 1,
  minWidth: 0,
});

export const photoContainerStyles = css.raw({
  display: { base: 'none', md: 'block' },
  // oxlint-disable-next-line constellation/no-raw-spacing-values
  width: '120px',
  height: '2000',
  flexShrink: 0,
  borderRadius: 'obj.default',
  overflow: 'hidden',
});

export const infoBlockStyles = css.raw({
  display: 'flex',
  flexDirection: 'column',
  gap: '100',
  flex: 1,
  minWidth: 0,
});

export const unitsTableContainerStyles = css.raw({
  borderTopWidth: 'default',
  borderTopStyle: 'solid',
  borderTopColor: 'border.muted',
});

export const unitCellStyles = css.raw({
  paddingLeft: '1200',
});

export const unitCellContentStyles = css.raw({
  display: 'flex',
  alignItems: 'center',
  gap: '300',
});

export const unitThumbnailStyles = css.raw({
  // Hide the thumbnail on small screens so the unit details get the full width.
  display: { base: 'none', md: 'block' },
  // oxlint-disable-next-line constellation/no-raw-spacing-values
  width: '120px',
  height: '1600',
  flexShrink: 0,
  borderRadius: 'obj.default',
  overflow: 'hidden',
});

export const unitInfoStyles = css.raw({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

// Single-line text that ellipsizes when it overflows; pair with TruncatedTextWithTooltip.
// Inherits the cursor so text inside a clickable row doesn't override the row's pointer.
export const truncatedTextStyles = css.raw({
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'inherit',
});

// Secondary columns collapse below `lg` so the table stays readable on narrow screens.
// Apply to both Table.Cell and Table.HeaderCell via the `css` prop to keep columns aligned.
export const hiddenMobileCellStyles = css.raw({
  display: { base: 'none', lg: 'table-cell' },
});

// Street line shown in the unit cell only while the Address column is collapsed, so units stay distinguishable.
export const mobileAddressTextStyles = css.raw({
  display: { base: 'block', lg: 'none' },
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'inherit',
});

// Overflow menu column keeps the trigger aligned to the right edge of the row.
export const overflowMenuCellStyle = css.raw({
  whiteSpace: 'nowrap',
  textAlign: 'right',
});

// Makes the row-wrapping Anchor behave as a normal table row (no layout box of its own)
// while keeping real anchor semantics: hovering shows the destination URL and cmd/ctrl-click
// opens it in a new tab.
export const propertyRowLinkStyles = css.raw({
  display: 'contents',
  color: 'inherit',
  textDecoration: 'none',
});

// Remove the bottom border on the final row
export const lastRowStyles = css.raw({
  '& td': {
    borderBlockEndWidth: '0',
  },
});

export const interactiveRowStyles = css.raw({
  cursor: 'pointer',
  _hover: { '& > td, & > th': { backgroundColor: 'bg.action.neutral.hover' } },
});
